import React from 'react';
import Markdown from 'react-markdown';

interface MarkdownEditorProps {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
}

function MarkdownEditor({
    value,
    onChange,
    placeholder,
}: MarkdownEditorProps): React.ReactElement {
    const [preview, setPreview] = React.useState(false);
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);

    const replace = (
        start: number,
        end: number,
        content: string,
        selectionStart: number,
        selectionEnd: number
    ): void => {
        onChange(value.slice(0, start) + content + value.slice(end));
        window.requestAnimationFrame(() => {
            textareaRef.current?.focus();
            textareaRef.current?.setSelectionRange(
                selectionStart,
                selectionEnd
            );
        });
    };

    const wrap = (before: string, after: string, fallback: string): void => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        const { selectionStart: start, selectionEnd: end } = textarea;
        const selected = value.slice(start, end) || fallback;
        replace(
            start,
            end,
            before + selected + after,
            start + before.length,
            start + before.length + selected.length
        );
    };

    const heading = (level: 1 | 2 | 3): void => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        const start = value.lastIndexOf('\n', textarea.selectionStart - 1) + 1;
        const nextBreak = value.indexOf('\n', textarea.selectionEnd);
        const end = nextBreak === -1 ? value.length : nextBreak;
        const selected = value.slice(start, end).replace(/^#{1,6}\s*/, '');
        const prefix = `${'#'.repeat(level)} `;
        const content = `${prefix}${selected || 'Заголовок'}`;
        replace(
            start,
            end,
            content,
            start + prefix.length,
            start + content.length
        );
    };

    const list = (numbered: boolean): void => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        const { selectionStart: start, selectionEnd: end } = textarea;
        const selected = value.slice(start, end) || 'Пункт списка';
        const content = selected
            .split('\n')
            .map((line, index) => {
                const clean = line.replace(/^([-*]|\d+\.)\s+/, '');
                return numbered ? `${index + 1}. ${clean}` : `- ${clean}`;
            })
            .join('\n');
        replace(start, end, content, start, start + content.length);
    };

    const link = (): void => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        const { selectionStart: start, selectionEnd: end } = textarea;
        const selected = value.slice(start, end) || 'текст ссылки';
        const content = `[${selected}](https://)`;
        const urlStart = start + selected.length + 3;
        replace(start, end, content, urlStart, urlStart + 8);
    };

    const quote = (): void => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        const start = value.lastIndexOf('\n', textarea.selectionStart - 1) + 1;
        const nextBreak = value.indexOf('\n', textarea.selectionEnd);
        const end = nextBreak === -1 ? value.length : nextBreak;
        const content = (value.slice(start, end) || 'Цитата')
            .split('\n')
            .map(line => `> ${line.replace(/^>\s?/, '')}`)
            .join('\n');
        replace(start, end, content, start + 2, start + content.length);
    };

    const divider = (): void => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        const position = textarea.selectionEnd;
        const content = '\n\n---\n\n';
        replace(
            position,
            position,
            content,
            position + content.length,
            position + content.length
        );
    };

    return (
        <div className="rd-md-editor">
            <div className="rd-md-editor__top">
                <span>Предвыборная программа</span>
                <div className="rd-md-editor__tabs">
                    <button
                        type="button"
                        aria-pressed={!preview}
                        onClick={() => setPreview(false)}
                    >
                        Написать
                    </button>
                    <button
                        type="button"
                        aria-pressed={preview}
                        onClick={() => setPreview(true)}
                    >
                        Предпросмотр
                    </button>
                </div>
            </div>
            {!preview ? (
                <>
                    <div
                        className="rd-md-editor__toolbar"
                        aria-label="Форматирование программы"
                    >
                        <button
                            type="button"
                            onMouseDown={event => event.preventDefault()}
                            onClick={() => heading(1)}
                            aria-label="Заголовок первого уровня"
                        >
                            H1
                        </button>
                        <button
                            type="button"
                            onMouseDown={event => event.preventDefault()}
                            onClick={() => heading(2)}
                            aria-label="Заголовок второго уровня"
                        >
                            H2
                        </button>
                        <button
                            type="button"
                            onMouseDown={event => event.preventDefault()}
                            onClick={() => heading(3)}
                            aria-label="Заголовок третьего уровня"
                        >
                            H3
                        </button>
                        <button
                            type="button"
                            onMouseDown={event => event.preventDefault()}
                            onClick={() => wrap('**', '**', 'жирный текст')}
                            aria-label="Жирный текст"
                        >
                            <strong>B</strong>
                        </button>
                        <button
                            type="button"
                            onMouseDown={event => event.preventDefault()}
                            onClick={() => wrap('*', '*', 'курсив')}
                            aria-label="Курсив"
                        >
                            <em>I</em>
                        </button>
                        <button
                            type="button"
                            onMouseDown={event => event.preventDefault()}
                            onClick={() => list(false)}
                        >
                            • Список
                        </button>
                        <button
                            type="button"
                            onMouseDown={event => event.preventDefault()}
                            onClick={() => list(true)}
                        >
                            1. Список
                        </button>
                        <button
                            type="button"
                            onMouseDown={event => event.preventDefault()}
                            onClick={quote}
                        >
                            Цитата
                        </button>
                        <button
                            type="button"
                            onMouseDown={event => event.preventDefault()}
                            onClick={link}
                        >
                            Ссылка
                        </button>
                        <button
                            type="button"
                            onMouseDown={event => event.preventDefault()}
                            onClick={divider}
                            aria-label="Разделитель"
                        >
                            Линия
                        </button>
                    </div>
                    <textarea
                        ref={textareaRef}
                        id="candidate-program"
                        aria-label="Предвыборная программа"
                        rows={14}
                        value={value}
                        placeholder={placeholder}
                        onChange={event => onChange(event.target.value)}
                    />
                </>
            ) : (
                <div className="rd-md-editor__preview rd-candidate__markdown">
                    {value.trim() ? (
                        <Markdown>{value}</Markdown>
                    ) : (
                        <p>Здесь появится твоя программа с оформлением.</p>
                    )}
                </div>
            )}
            <p className="rd-md-editor__count">{value.length} символов</p>
        </div>
    );
}

export default React.memo(MarkdownEditor);
